/* eslint-disable @typescript-eslint/no-namespace */
import type * as CSS from 'csstype';

export namespace Breakpoints {
  /** Breakpoint Variant Keys */
  export type VariantKeys = 'bp0' | 'bp500' | 'bp768' | 'bp900' | 'bp1024' | 'bp1220' | 'bp1440' | 'bp1728';

  /** Heading Variants */
  export type Variants = {
    [Key in VariantKeys]: Size.VariantKeys;
  };

  /** Breakpoint Size */
  export namespace Size {
    /** Breakpoint Size Variant Keys */
    export type VariantKeys = '0' | '500px' | '768px' | '900px' | '1024px' | '1220px' | '1440px' | '1728px';

    export type Variants = {
      [Key in Breakpoints.VariantKeys]: Key extends 'bp0'
        ? '0'
        : Key extends 'bp500'
          ? '500px'
          : Key extends 'bp768'
            ? '768px'
            : Key extends 'bp900'
              ? '900px'
              : Key extends 'bp1024'
                ? '1024px'
                : Key extends 'bp1220'
                  ? '1220px'
                  : Key extends 'bp1440'
                    ? '1440px'
                    : Key extends 'bp1728'
                      ? '1728px'
                      : never;
    };
  }

  /** Breakpoint Media Query Size */
  export namespace MediaQuery {
    /** Breakpoint Media Query Size Variant Keys */
    export type VariantKeys = 'min' | 'max' | 'px';

    /** Breakpoints Media Query Variants */
    export type Variants = {
      [Key in Breakpoints.VariantKeys]: {
        [Key in VariantKeys]: Key extends 'px' ? Size.VariantKeys : CSS.AtRules;
      };
    };
  }
}

export const breakpointsValues: Breakpoints.Size.VariantKeys[] = [
  '0',
  '500px',
  '768px',
  '900px',
  '1024px',
  '1220px',
  '1440px',
  '1728px',
];

type MediaQueryBreakpoint = (value: Breakpoints.Size.VariantKeys) => {
  [Key in Breakpoints.MediaQuery.VariantKeys]: Key extends 'px' ? Breakpoints.Size.VariantKeys : CSS.AtRules;
};

const createMediaQueryBreakpoints: MediaQueryBreakpoint = value => ({
  min: `@media only screen and (min-width: ${value})` as CSS.AtRules,
  max: `@media only screen and (max-width: ${value})` as CSS.AtRules,
  px: value,
});

const breakpoints: Breakpoints.MediaQuery.Variants = {
  /** @returns `0` */
  bp0: createMediaQueryBreakpoints(breakpointsValues[0]),
  /** @returns `500px` */
  bp500: createMediaQueryBreakpoints(breakpointsValues[1]),
  /** @returns `768px` */
  bp768: createMediaQueryBreakpoints(breakpointsValues[2]),
  /** @returns `900px` */
  bp900: createMediaQueryBreakpoints(breakpointsValues[3]),
  /** @returns `1024px` */
  bp1024: createMediaQueryBreakpoints(breakpointsValues[4]),
  /** @returns `1220px` */
  bp1220: createMediaQueryBreakpoints(breakpointsValues[5]),
  /** @returns `1440px` */
  bp1440: createMediaQueryBreakpoints(breakpointsValues[6]),
  /** @returns `1728px` */
  bp1728: createMediaQueryBreakpoints(breakpointsValues[7]),
};

export const breakpointsVariants = Object.keys(breakpointsValues) as Breakpoints.VariantKeys[];

const BP_0 = breakpointsValues[0];
const BP_500 = breakpointsValues[1];
const BP_768 = breakpointsValues[2];
const BP_900 = breakpointsValues[3];
const BP_1024 = breakpointsValues[4];
const BP_1220 = breakpointsValues[5];
const BP_1440 = breakpointsValues[6];
const BP_1728 = breakpointsValues[7];

export const BREAKPOINT = {
  /** @returns `0` */
  BP_0,
  /** @returns `500px` */
  BP_500,
  /** @returns `768px` */
  BP_768,
  /** @returns `900px` */
  BP_900,
  /** @returns `1024px` */
  BP_1024,
  /** @returns `1220px` */
  BP_1220,
  /** @returns `1440px` */
  BP_1440,
  /** @returns `1728px` */
  BP_1728,
};

export { breakpoints };
export default breakpoints;
